import { motion } from "framer-motion";
import { Phone, Plane, Flame } from "lucide-react";
import { PHONE_TEL } from "@/lib/site";
import { useCurrency } from "@/context/CurrencyContext";
import { OfferCountdown } from "./OfferCountdown";

export const RouteCard = ({ route, index = 0, hot = false }) => {
  const { format } = useCurrency();
  return (
    <motion.a
      href={PHONE_TEL}
      initial={{ opacity: 0, y: 18, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, delay: Math.min(index, 12) * 0.035, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4, rotate: -0.6 }} whileTap={{ scale: 0.98 }}
      data-testid={`route-card-${route.id}`}
      aria-label={`Call to check fare from ${route.origin_city} to ${route.destination_city}`}
      className={`group relative flex items-center gap-3 overflow-hidden rounded-2xl border bg-white p-2.5 pr-3 transition-[border-color,box-shadow] duration-300 hover:border-coral/50 hover:shadow-[0_14px_30px_-14px_rgba(14,42,71,0.35)] ${hot ? "border-coral/40 ring-1 ring-coral/20" : "border-slate-200"}`}
    >
      {hot && (
        <span className="absolute right-0 top-0 z-10 flex items-center gap-1 rounded-bl-xl bg-coral px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white" data-testid={`route-hot-${route.id}`}>
          <Flame className="h-3 w-3 animate-pulse" /> Hot deal
        </span>
      )}
      <span className="absolute inset-y-0 left-0 w-1 origin-top scale-y-0 bg-coral transition-transform duration-300 group-hover:scale-y-100" />
      <div className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl bg-brand-soft">
        {route.image && <img src={route.image} alt={`${route.destination_city} destination view`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:rotate-3 group-hover:scale-125" />}
        <span className="absolute inset-x-0 bottom-0 bg-ink/75 py-0.5 text-center text-[8px] font-extrabold uppercase tracking-wider text-white">Promo fare</span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] text-slate-500">From {route.origin_city}</p>
        <h3 className="truncate font-display text-[15px] font-bold leading-tight text-ink">{route.destination_city}</h3>
        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-extrabold tracking-wide text-slate-600">
          <span>{route.origin_code}</span>
          <span className="relative h-3 flex-1">
            <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-slate-300" />
            <Plane className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 text-brand transition-[left,color] duration-700 ease-out [left:0%] group-hover:text-coral group-hover:[left:calc(100%-12px)]" />
          </span>
          <span>{route.destination_code}</span>
        </div>
        <p className="mt-1 truncate text-[10px] text-slate-500 group-hover:hidden" title={route.airlines}>{route.airlines}</p>
        <p className="mt-1 hidden truncate text-[10px] font-bold text-coral group-hover:block">Tap to call &amp; unlock this fare</p>
        {hot && <OfferCountdown endsAt={route.offer_ends} testId={`route-countdown-${route.id}`} />}
      </div>

      <div className={`flex shrink-0 flex-col items-end gap-1.5 ${hot ? "pt-3" : ""}`}>
        <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400">from</span>
        <span className="-mt-1.5 font-display text-lg font-extrabold text-ink transition-colors group-hover:text-coral" data-testid={`route-price-${route.id}`}>{format(route.price)}</span>
        <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-soft text-brand transition-[background-color,color,transform] duration-300 group-hover:rotate-12 group-hover:scale-110 group-hover:bg-coral group-hover:text-white" data-testid={`route-call-${route.id}`}>
          <Phone className="h-3.5 w-3.5" />
        </span>
      </div>
    </motion.a>
  );
};
